import apiClient from './apiClient';
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
} from '../types/auth';

/**
 * Authentication Service
 *
 * Security note: Tokens are stored in both localStorage and cookies.
 * Cookies use SameSite=Strict and Secure (on HTTPS) but cannot be httpOnly
 * because they are set client-side. For production, tokens should be set
 * as httpOnly cookies by the backend API.
 *
 * TODO: Migrate to server-set httpOnly cookies when backend supports it.
 */
class AuthService {
  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', data);

      if (response.data.success && response.data.data) {
        // Save tokens to localStorage and cookies (web only)
        if (typeof window !== 'undefined') {
          const { accessToken, refreshToken, user } = response.data.data;

          // localStorage
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('user', JSON.stringify(this.sanitizeUser(user)));

          // 쿠키에도 저장 (middleware에서 사용)
          const isSecure = window.location.protocol === 'https:';
          const securePart = isSecure ? '; Secure' : '';
          document.cookie = `accessToken=${accessToken}; path=/; max-age=${15 * 60}; SameSite=Strict${securePart}`;
          document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Strict${securePart}`;
        }
      }

      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; code?: string; message?: string };
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        return {
          success: false,
          error: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
        };
      }
      return {
        success: false,
        error: err.response?.data?.error || '회원가입에 실패했습니다. 입력 정보를 확인해주세요.',
      };
    }
  }

  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials);

      if (response.data.success && response.data.data) {
        // Save tokens to localStorage and cookies (web only)
        if (typeof window !== 'undefined') {
          const { accessToken, refreshToken, user } = response.data.data;

          // localStorage
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('user', JSON.stringify(this.sanitizeUser(user)));

          // 쿠키에도 저장 (middleware에서 사용)
          const isSecure = window.location.protocol === 'https:';
          const securePart = isSecure ? '; Secure' : '';
          document.cookie = `accessToken=${accessToken}; path=/; max-age=${15 * 60}; SameSite=Strict${securePart}`;
          document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Strict${securePart}`;
        }
      }

      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; code?: string; message?: string };
      // Network error (backend not running)
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        return {
          success: false,
          error: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
        };
      }
      return {
        success: false,
        error: err.response?.data?.error || '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.',
      };
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Logout API call failed, proceed with local cleanup
    } finally {
      // Clear tokens from localStorage and cookies
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        // 쿠키 삭제
        const isSecure = window.location.protocol === 'https:';
        const securePart = isSecure ? '; Secure' : '';
        document.cookie = `accessToken=; path=/; max-age=0; SameSite=Strict${securePart}`;
        document.cookie = `refreshToken=; path=/; max-age=0; SameSite=Strict${securePart}`;

        // Clear browser caches (cached API responses, etc.)
        if ('caches' in window) {
          caches.keys().then(names => names.forEach(name => caches.delete(name)));
        }
      }
    }
  }

  /**
   * Sanitize user object before storing in localStorage.
   * Only keep fields needed for UI — strip PII like phone, email, birth_date.
   */
  private sanitizeUser(user: User): Partial<User> {
    return {
      id: user.id,
      username: user.username,
      full_name: user.full_name,
      account_type: user.account_type,
      grade: user.grade,
      school_name: user.school_name,
    };
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User }>('/auth/me');
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (_error: unknown) {
      return null;
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/refresh', {
        refreshToken,
      });

      if (response.data.success && response.data.data) {
        // Update tokens in localStorage and cookies
        if (typeof window !== 'undefined') {
          localStorage.setItem('accessToken', response.data.data.accessToken);
          localStorage.setItem('refreshToken', response.data.data.refreshToken);

          const isSecure = window.location.protocol === 'https:';
          const securePart = isSecure ? '; Secure' : '';
          document.cookie = `accessToken=${response.data.data.accessToken}; path=/; max-age=${15 * 60}; SameSite=Strict${securePart}`;
          document.cookie = `refreshToken=${response.data.data.refreshToken}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Strict${securePart}`;
        }
      }

      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: err.response?.data?.error || 'Token refresh failed',
      };
    }
  }

  /**
   * Check if user is authenticated (client-side only)
   */
  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;
    const accessToken = localStorage.getItem('accessToken');
    return !!accessToken;
  }

  /**
   * Get stored user from localStorage (client-side only)
   */
  getStoredUser(): User | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Get stored access token (client-side only)
   */
  getStoredAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken');
  }

  /**
   * Get stored refresh token (client-side only)
   */
  getStoredRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refreshToken');
  }
}

export default new AuthService();
